/**
 * Who may see what, as plain functions.
 *
 * Kept apart from index.tsx deliberately. That file imports react-router-dom
 * and MUI, which the test environment cannot resolve, so any test that reached
 * this logic through the component failed to run at all. The rule itself is a
 * pure function of the permissions object and belongs where it can be read and
 * tested on its own -- the same reason the sidemenu rules live outside the
 * sidemenu component.
 */

export type PermissionAction = "view" | "edit";

/**
 * Edit implies view. A role with edit and no view flag must still be able to
 * open the page -- otherwise granting someone edit would hide the thing they
 * were just given permission to change.
 */
export const canView = (permissions: any, module: string): boolean =>
  permissions?.[module]?.view === true || permissions?.[module]?.edit === true;

export const canEdit = (permissions: any, module: string): boolean =>
  permissions?.[module]?.edit === true;

export const isAllowed = (
  permissions: any,
  module: string,
  action: PermissionAction = "view",
): boolean =>
  action === "edit" ? canEdit(permissions, module) : canView(permissions, module);

/**
 * Where to send an employee after login, in priority order.
 *
 * The dashboard is the natural home for anyone who can see it, but it is
 * commercial data, so products is the fallback -- the module most employees
 * hold. Anything they can view beats a page they cannot.
 */
export const LANDING_PRIORITY: Array<{ module: string; route: string }> = [
  { module: "dashboard", route: "/dashboard" },
  { module: "products", route: "/products" },
  { module: "orders", route: "/orders" },
  { module: "categories", route: "/categories" },
  { module: "customers", route: "/customers" },
  { module: "inventories", route: "/inventory-validate" },
  { module: "supplier", route: "/supplier" },
  { module: "reports", route: "/product-reports" },
  { module: "employee", route: "/employee" },
  { module: "roles", route: "/roles" },
  { module: "customer-help-service", route: "/customer-help-service" },
  { module: "home-screen", route: "/home-screen" },
  { module: "cms", route: "/cms" },
  { module: "profile", route: "/taxation" },
  { module: "settings", route: "/adminSettings" },
];

export const getFirstPermittedRoute = (permissions: any): string => {
  const hit = LANDING_PRIORITY.find((entry) => canView(permissions, entry.module));
  // No module at all: land on the dashboard and let the guard explain. Better a
  // clear message than a redirect loop between forbidden routes.
  return hit ? hit.route : "/dashboard";
};
